import { useState, useEffect } from "react";
import { useAuth } from "../../../hooks/useAuth";
import toast from "react-hot-toast";
import "./ViewTicket.css";
import Details from "./Details";
import CommentBox from "./CommentBox";
import AddAttachments from "./AddAttachments";
import ConfirmDialog from "./ConfirmDialog";
import TicketStatus from "../TicketStatus/TicketStatus";
import getTime from "../../../helpers/getTime.js";
import { useParams, useNavigate } from "react-router-dom";
import { getTicketById, changeTicketStatus, deleteTicket, updateTicket } from "../../../api/ticketApi.js";

/**
 * View Ticket Details Component
 * Updated for Auth Microservice integration - v2.0
 */
const ViewTicket = () => {
    const { user, isSuperAdmin } = useAuth();
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isDeletingTicket, setIsDeletingTicket] = useState(false);
    const [showAddAttachments, setShowAddAttachments] = useState(false);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [editedTitle, setEditedTitle] = useState("");
    const [editedDescription, setEditedDescription] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (id) {
            fetchTicketData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, refreshKey]);

    /**
     * Fetch ticket data by ID
     */
    const fetchTicketData = async () => {
        try {
            setLoading(true);
            const response = await getTicketById(id);
            
            if (response.success) {
                setTicket(response.data);
            } else {
                toast.error(response.message || "Failed to load ticket");
            }
        } catch (error) {
            console.error("Error fetching ticket:", error);
            // Extract proper error message from API response
            let errorMessage = "Failed to load ticket details";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Refresh ticket data
     */
    const refreshTicket = () => {
        setRefreshKey(prev => prev + 1);
    };

    /**
     * Handle attachments added callback
     */
    const handleAttachmentsAdded = () => {
        refreshTicket();
        setShowAddAttachments(false);
    };

    /**
     * Change ticket status (All users)
     */
    const handleStatusChange = async (newStatus) => {
        try {
            const response = await changeTicketStatus(id, newStatus);
            
            if (response.success) {
                toast.success(`Ticket status changed to ${newStatus}`);
                refreshTicket();
            } else {
                toast.error(response.message || "Failed to change status");
            }
        } catch (error) {
            console.error("Error changing status:", error);
            // Extract proper error message from API response
            let errorMessage = "Failed to change ticket status";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        }
    };

    /**
     * Handle priority change
     */
    const handlePriorityChange = async (newPriority) => {
        try {
            const response = await updateTicket(id, { priority: newPriority });
            
            if (response.success) {
                toast.success(`Priority changed to ${newPriority}`);
                refreshTicket();
            } else {
                toast.error(response.message || "Failed to change priority");
            }
        } catch (error) {
            console.error("Error changing priority:", error);
            let errorMessage = "Failed to change priority";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        }
    };

    /**
     * Handle title save
     */
    const handleSaveTitle = async () => {
        if (!editedTitle.trim()) {
            toast.error("Title cannot be empty");
            return;
        }
        
        try {
            setIsSaving(true);
            const response = await updateTicket(id, { subject: editedTitle.trim() });
            
            if (response.success) {
                toast.success("Title updated successfully");
                setIsEditingTitle(false);
                refreshTicket();
            } else {
                toast.error(response.message || "Failed to update title");
            }
        } catch (error) {
            console.error("Error updating title:", error);
            let errorMessage = "Failed to update title";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Handle description save
     */
    const handleSaveDescription = async () => {
        if (!editedDescription.trim()) {
            toast.error("Description cannot be empty");
            return;
        }
        
        try {
            setIsSaving(true);
            const response = await updateTicket(id, { description: editedDescription.trim() });
            
            if (response.success) {
                toast.success("Description updated successfully");
                setIsEditingDescription(false);
                refreshTicket();
            } else {
                toast.error(response.message || "Failed to update description");
            }
        } catch (error) {
            console.error("Error updating description:", error);
            let errorMessage = "Failed to update description";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Initialize edit states when ticket loads
     */
    useEffect(() => {
        if (ticket) {
            setEditedTitle(ticket.subject || "");
            setEditedDescription(ticket.description || "");
        }
    }, [ticket]);

    /**
     * Delete ticket
     */
    const handleDeleteTicket = async () => {
        try {
            setIsDeletingTicket(true);
            const response = await deleteTicket(id);
            
            if (response.success) {
                toast.success("Ticket deleted successfully");
                setShowDeleteDialog(false);
                setTimeout(() => navigate("/my-tickets"), 1000);
            } else {
                toast.error(response.message || "Failed to delete ticket");
            }
        } catch (error) {
            console.error("Error deleting ticket:", error);
            // Extract proper error message from API response
            let errorMessage = "Failed to delete ticket";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        } finally {
            setIsDeletingTicket(false);
        }
    };

    if (loading) {
        return (
            <div className="view-ticket" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                <div style={{ textAlign: "center" }}>
                    <i className="las la-spinner la-spin" style={{ fontSize: "48px", color: "#3bac75" }} />
                    <p style={{ marginTop: "10px", color: "#6c757d" }}>Loading ticket details...</p>
                </div>
            </div>
        );
    }

    if (!ticket) {
        return (
            <div className="view-ticket" style={{ textAlign: "center", padding: "40px" }}>
                <i className="las la-exclamation-triangle" style={{ fontSize: "48px", color: "#ffa500" }} />
                <p style={{ marginTop: "10px", color: "#6c757d" }}>Ticket not found</p>
                <button onClick={() => navigate("/tickets")} style={{ marginTop: "20px", padding: "10px 20px" }}>
                    Back to Tickets
                </button>
            </div>
        );
    }

    const isOwner = ticket.creatorId === user?.id;
    const canManage = isSuperAdmin || isOwner;

    const getPriorityColor = (priority) => {
        const colors = {
            low: "#28a745",
            medium: "#ffa500",
            high: "#ff6b6b",
            urgent: "#dc3545",
        };
        return colors[priority] || "#6c757d";
    };

    return (
        <div className="view-ticket" style={{
            padding: "24px",
            background: "#ffffff",
            minHeight: "100vh",
            width: "100%",
            display: "flex",
            gap: "24px",
            alignItems: "flex-start"
        }}>
            {/* Main Content Area */}
            <div style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "24px"
        }}>
            {/* Modern Header with Back Button */}
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
                paddingBottom: "20px",
                borderBottom: "1px solid #e5e7eb"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            background: "transparent",
                            color: "#4b5563",
                            border: "1px solid #d1d5db",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: "12.6px",
                            transition: "all 0.15s"
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f3f4f6";
                            e.currentTarget.style.borderColor = "#9ca3af";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                            e.currentTarget.style.borderColor = "#d1d5db";
                        }}
                    >
                        <i className="las la-arrow-left" />
                        Back
                    </button>
                    {isEditingTitle ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
                            <input
                                type="text"
                                value={editedTitle}
                                onChange={(e) => setEditedTitle(e.target.value)}
                                style={{
                                    flex: 1,
                                    padding: "8px 12px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "6px",
                                    fontSize: "21.6px",
                                    fontWeight: "600",
                                    color: "#111827"
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        handleSaveTitle();
                                    } else if (e.key === "Escape") {
                                        setIsEditingTitle(false);
                                        setEditedTitle(ticket?.subject || "");
                                    }
                                }}
                                autoFocus
                            />
                            <button
                                onClick={handleSaveTitle}
                                disabled={isSaving}
                                style={{
                                    padding: "6px 12px",
                                    background: "#3bac75",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor: isSaving ? "not-allowed" : "pointer",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    opacity: isSaving ? 0.6 : 1
                                }}
                            >
                                <i className="las la-check" /> {isSaving ? "Saving..." : "Save"}
                            </button>
                            <button
                                onClick={() => {
                                    setIsEditingTitle(false);
                                    setEditedTitle(ticket?.subject || "");
                                }}
                                disabled={isSaving}
                                style={{
                                    padding: "6px 12px",
                                    background: "transparent",
                                    color: "#6b7280",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "6px",
                                    cursor: isSaving ? "not-allowed" : "pointer",
                                    fontSize: "12px",
                                    fontWeight: "500"
                                }}
                            >
                                <i className="las la-times" />
                            </button>
                        </div>
                    ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
                            <h1 style={{
                                margin: 0,
                                fontSize: "21.6px",
                                fontWeight: "600",
                                color: "#111827",
                                flex: 1
                            }}>
                                {ticket?.subject || "Loading..."}
                            </h1>
                            {(isSuperAdmin || isOwner) && (
                    <button
                                    onClick={() => setIsEditingTitle(true)}
                        style={{
                                        padding: "6px 10px",
                            background: "transparent",
                                        color: "#6b7280",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "6px",
                            cursor: "pointer",
                                        fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                                        gap: "4px"
                                    }}
                                    title="Edit title"
                                >
                                    <i className="las la-pen" />
                    </button>
                            )}
                        </div>
                )}
                </div>

            </div>

            {/* Ticket Details */}
            <Details 
                ticket={ticket} 
                showAddAttachmentsButton={(isSuperAdmin || ticket?.creatorId === user?.id || ticket?.assignedToId === user?.id)}
                onAddAttachmentsClick={() => setShowAddAttachments(true)}
                onDescriptionUpdated={refreshTicket}
            />

                {/* Comments & Activity Logs Section (with tabs) */}
                <CommentBox
                    ticket={ticket}
                    ticketId={id}
                    onCommentAdded={refreshTicket}
                />
            </div>

            {/* Right Sidebar - Ticket Metadata */}
            <div style={{
                width: "320px",
                flexShrink: 0,
                background: "rgb(255, 255, 255)",
                borderRadius: "12px",
                border: "1px solid rgb(229, 231, 235)",
                padding: "24px",
                marginBottom: "24px",
                boxShadow: "rgba(0, 0, 0, 0.1) 0px 1px 3px"
            }}>
                {/* Priority */}
                <div style={{
                    marginBottom: "20px",
                    paddingBottom: "20px",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <div style={{
                        fontSize: "9.9px",
                        color: "#6b7280",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                    }}>
                        Priority
                    </div>
                    <div>
                        <select
                            value={ticket.priority || "medium"}
                            onChange={(e) => handlePriorityChange(e.target.value)}
                            style={{
                                padding: "6px 14px",
                                borderRadius: "6px",
                                fontSize: "10.8px",
                                fontWeight: "600",
                                textTransform: "capitalize",
                                border: "1px solid #d1d5db",
                                cursor: "pointer",
                                outline: "none",
                                background: "white",
                                color: "#111827"
                            }}
                        >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                        </select>
                    </div>
                </div>

                {/* Status */}
                <div style={{
                    marginBottom: "20px",
                    paddingBottom: "20px",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <div style={{
                        fontSize: "9.9px",
                        color: "#6b7280",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                    }}>
                        Status
                    </div>
                    <div>
                        <select
                            value={ticket.status || "open"}
                            onChange={(e) => handleStatusChange(e.target.value)}
                            style={{
                                padding: "6px 14px",
                                borderRadius: "6px",
                                fontSize: "10.8px",
                                fontWeight: "600",
                                textTransform: "capitalize",
                                border: "1px solid #d1d5db",
                                cursor: "pointer",
                                outline: "none",
                                background: "white",
                                color: "#111827"
                            }}
                        >
                            <option value="open">Open</option>
                            <option value="in_progress">In Progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                        </select>
                    </div>
                </div>

                {/* Category */}
                {ticket.category && (
                    <div style={{
                        marginBottom: "20px",
                        paddingBottom: "20px",
                        borderBottom: "1px solid #e5e7eb",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                    }}>
                        <div style={{
                            fontSize: "9.9px",
                            color: "#6b7280",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                        }}>
                            Category
                        </div>
                        <div style={{
                            fontWeight: "600",
                            fontSize: "12.6px",
                            color: "#111827"
                        }}>
                            {ticket.category}
                        </div>
                    </div>
                )}

                {/* Ticket Number */}
                <div style={{
                    marginBottom: "20px",
                    paddingBottom: "20px",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <div style={{
                        fontSize: "9.9px",
                        color: "#6b7280",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                    }}>
                        Ticket Number
                    </div>
                    <div style={{
                        color: "#3bac75",
                        fontSize: "13.5px",
                        fontWeight: "700",
                        letterSpacing: "0.5px"
                    }}>
                        {ticket.ticketNumber}
                    </div>
                </div>

                {/* Created By */}
                <div style={{
                    marginBottom: "20px",
                    paddingBottom: "20px",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start"
                }}>
                    <div style={{
                        fontSize: "9.9px",
                        color: "#6b7280",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                    }}>
                        Created By
                    </div>
                    <div style={{
                        textAlign: "right"
                    }}>
                        <div style={{
                            fontWeight: "600",
                            fontSize: "12.6px",
                            color: "#111827",
                            marginBottom: "4px"
                        }}>
                            {ticket.creatorName}
                        </div>
                        <div style={{
                            fontSize: "10.8px",
                            color: "#6b7280"
                        }}>
                            {ticket.creatorEmail}
                        </div>
                    </div>
                </div>

                {/* Created */}
                <div style={{
                    marginBottom: "20px",
                    paddingBottom: "20px",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <div style={{
                        fontSize: "9.9px",
                        color: "#6b7280",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                    }}>
                        Created
                    </div>
                    <div style={{
                        fontWeight: "500",
                        fontSize: "12.6px",
                        color: "#111827"
                    }}>
                        {getTime(ticket.created_at || ticket.createdAt)}
                    </div>
                </div>

                {/* Issue Found */}
                <div style={{
                    marginBottom: "20px",
                    paddingBottom: "20px",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <div style={{
                        fontSize: "9.9px",
                        color: "#6b7280",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                    }}>
                        Issue Found
                    </div>
                    <div style={{
                        fontWeight: "500",
                        fontSize: "12.6px",
                        color: "#111827"
                    }}>
                        {getTime(ticket.issueFoundAt)}
                    </div>
                </div>

                {/* Due Date */}
                <div style={{
                    marginBottom: "20px",
                    paddingBottom: "20px",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <div style={{
                        fontSize: "9.9px",
                        color: "#6b7280",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                    }}>
                        Due Date
                    </div>
                    {ticket.dueDate ? (
                        <div style={{
                            color: "#dc2626",
                            fontWeight: "600",
                            fontSize: "12.6px"
                        }}>
                            {getTime(ticket.dueDate)}
                        </div>
                    ) : (
                        <div style={{
                            color: "#9ca3af",
                            fontWeight: "500",
                            fontSize: "12.6px",
                            fontStyle: "italic"
                        }}>
                            Not set
                        </div>
                    )}
                </div>

                {/* Resolved */}
                <div style={{
                    marginBottom: "20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <div style={{
                        fontSize: "9.9px",
                        color: "#6b7280",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                    }}>
                        Resolved
                    </div>
                    {ticket.resolvedAt ? (
                        <div style={{
                            color: "#16a34a",
                            fontWeight: "600",
                            fontSize: "12.6px"
                        }}>
                            {getTime(ticket.resolvedAt)}
                        </div>
                    ) : (
                        <div style={{
                            color: "#9ca3af",
                            fontWeight: "500",
                            fontSize: "12.6px",
                            fontStyle: "italic"
                        }}>
                            Not resolved
                        </div>
                    )}
                </div>

                {/* Assigned To (if exists) */}
                {ticket.assignedToName && (
                    <div style={{
                        marginTop: "20px",
                        paddingTop: "20px",
                        borderTop: "1px solid #e5e7eb",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                    }}>
                        <div style={{
                            fontSize: "9.9px",
                            color: "#6b7280",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                        }}>
                            Assigned To
                        </div>
                        <div style={{
                            color: "#3bac75",
                            fontWeight: "600",
                            fontSize: "12.6px"
                        }}>
                            {ticket.assignedToName}
                        </div>
                    </div>
                )}
            </div>

            <ConfirmDialog
                open={showDeleteDialog}
                title="Delete this ticket?"
                message="This will permanently remove the ticket and all of its activity. You won't be able to restore it."
                confirmLabel="Delete Ticket"
                cancelLabel="Keep Ticket"
                loading={isDeletingTicket}
                onConfirm={handleDeleteTicket}
                onCancel={() => {
                    if (!isDeletingTicket) {
                        setShowDeleteDialog(false);
                    }
                }}
            />

            {/* Add Attachments Dialog */}
            {showAddAttachments && (
                <AddAttachments
                    ticketId={id}
                    onAttachmentsAdded={handleAttachmentsAdded}
                    onClose={() => setShowAddAttachments(false)}
                />
            )}
        </div>
    );
};

export default ViewTicket;
